import { ReactElement, useState } from "react";
import { DropOverProps } from "../DropOver/DropOver";
import { DropOverActionList } from "../DropOverActionList/DropOverActionList";
import { localize } from "client_legacy/ndaw/culture";
import { ActionListItem } from "../ActionList/ActionListItem";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { ActionListSubMenuHeader } from "../ActionList/ActionListSubmenuHeader";
import { never } from "client_legacy/ndaw/errors";
import { PreviewMenu, PreviewMenuContentDefinition } from "./PreviewMenu";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { generateTrackingId } from "client/Misc/utils";
import { RewriteToneKind, TranslateLangKind } from "client/Api/AiAssistant/aiAssistant";
import {
	RewriteToolHandlers,
	RewriteToolResponse,
	SuccessfulRewriteToolResponse
} from "./common";

export {
	usePlainTextRewriteTools,
	type RewriteToolsService
} from "./usePlainTextRewriteTools";
export {
	type RewriteToolResponse,
	type SuccessfulRewriteToolResponse,
	type ContentTooLargeRewriteToolResponse,
	type RewriteToolsResult
} from "./common";

export type RewriteToolsMenuProps<TSlice, TContent, TPosition> = {
	target: HTMLElement;
	targetAlignment?: DropOverProps["targetAlignment"];
	contentAlignment?: DropOverProps["contentAlignment"];
	onClose: () => void;
	trackingId: string;
	handlers: RewriteToolHandlers<TSlice, TContent, TPosition>;
};

export const RewriteToolsMenu = <TSlice, TContent, TPosition>(
	props: RewriteToolsMenuProps<TSlice, TContent, TPosition>
): ReactElement => {
	const {
		target,
		targetAlignment,
		contentAlignment,
		onClose,
		trackingId,
		handlers
	} = props;

	const [view, setView] = useState<View<TSlice, TContent, TPosition>>("main");

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const {
		makeShorter,
		makeLonger,
		fixSpelling,
		changeTone,
		translate,
		apply,
		getPreviewHtml,
		maxContentLength
	} = handlers;

	const executeToolActionHandler = async (
		operation: RewriteOperation,
		callback: (signal?: AbortSignal) => Promise<RewriteToolResponse<TSlice, TContent, TPosition>>
	): Promise<void> => {
		setView({
			kind: "preview-loading",
			operation
		});

		try {
			const response: RewriteToolResponse<TSlice, TContent, TPosition> = await callback(globalCancellation);

			switch (response.status) {
				case "success":
					setView({
						kind: "preview",
						operation,
						response: response,
						previewHtml: getPreviewHtml(response),
						retry: () => executeToolActionHandler(operation, callback)
					});
					break;
				case "content-too-large":
					setView({
						kind: "content-too-large",
						operation
					});
					break;
				default:
					never(response);
			}


		} catch(e) {
			if (isCancelledError(e)) {
				return;
			}

			setView({
				kind: "preview-error",
				operation,
				retry: () => executeToolActionHandler(operation, callback)
			});
		}
	};

	const executeOperation = async (operation: RewriteOperation): Promise<void> => {
		await executeToolActionHandler(
			operation,
			async (signal?: AbortSignal): Promise<RewriteToolResponse<TSlice, TContent, TPosition>> => {
				switch (operation.kind) {
						case "make-shorter":
							return await makeShorter(signal);
						case "make-longer":
							return await makeLonger(signal);
						case "fix-spelling":
							return await fixSpelling(signal);
						case "change-tone":
							return await changeTone(operation.tone, signal);
						case "translate":
							return await translate(operation.lang, signal);
						default:
							never(operation);
				}
			}
		);
	};

	const applyHandler = (response: SuccessfulRewriteToolResponse<TSlice, TContent, TPosition>): void => {
		try {
			apply(response);
			onClose();
		} catch(e) {
			alertManager.showError(localize("_GeneralErrorMessage"));
			console.error(e);
		}
	};

	switch (view) {
		case "main":
			return (
				<DropOverActionList
					target={target}
					menuTitle={localize("_ImproveText")}
					trackingId={trackingId}
					onClose={onClose}
					targetAlignment={targetAlignment}
					contentAlignment={contentAlignment}
					hasResponsiveDropOver={false}
					shouldStayOpenOnScroll
				>
					<ActionListItem
						text={localize("_MakeShorter")}
						trackingId="make-shorter"
						onClick={() => executeOperation({ kind: "make-shorter" })}
						shouldNotAutoCloseContainingDropOver
					/>
					<ActionListItem
						text={localize("_MakeLonger")}
						trackingId="make-longer"
						onClick={() => executeOperation({ kind: "make-longer" })}
						shouldNotAutoCloseContainingDropOver
					/>
					<ActionListItem
						text={localize("_FixSpellingAndGrammar")}
						trackingId="fix-spelling"
						onClick={() => executeOperation({ kind: "fix-spelling" })}
						shouldNotAutoCloseContainingDropOver
					/>
					<ActionListItem
						text={localize("_ChangeTone")}
						trackingId="change-tone"
						onClick={() => setView("tone")}
						rightIcon="chevron-right"
						shouldNotAutoCloseContainingDropOver
					/>
					<ActionListItem
						text={localize("_Translate")}
						trackingId="translate"
						onClick={() => setView("lang")}
						rightIcon="chevron-right"
						shouldNotAutoCloseContainingDropOver
					/>
				</DropOverActionList>
			);
		case "tone":
			return (
				<DropOverActionList
					target={target}
					menuTitle={localize("_ChangeTone")}
					trackingId={trackingId}
					onClose={onClose}
					targetAlignment={targetAlignment}
					contentAlignment={contentAlignment}
					hasResponsiveDropOver={false}
					shouldStayOpenOnScroll
				>
					<ActionListSubMenuHeader
						text={localize("_ChangeTone")}
						onGoBack={() => setView("main")}
					/>
					<ChangeToneItem tone={RewriteToneKind.Professional} onExecute={executeOperation} />
					<ChangeToneItem tone={RewriteToneKind.Casual} onExecute={executeOperation} />
					<ChangeToneItem tone={RewriteToneKind.Empathic} onExecute={executeOperation} />
				</DropOverActionList>
			);
		case "lang":
			return (
				<DropOverActionList
					target={target}
					menuTitle={localize("_Translate")}
					trackingId={trackingId}
					onClose={onClose}
					targetAlignment={targetAlignment}
					contentAlignment={contentAlignment}
					hasResponsiveDropOver={false}
					shouldStayOpenOnScroll
				>
					<ActionListSubMenuHeader
						text={localize("_Translate")}
						onGoBack={() => setView("main")}
					/>
					<TranslateItem lang={TranslateLangKind.Dutch} onExecute={executeOperation} />
					<TranslateItem lang={TranslateLangKind.French} onExecute={executeOperation} />
					<TranslateItem lang={TranslateLangKind.German} onExecute={executeOperation} />
					<TranslateItem lang={TranslateLangKind.English} onExecute={executeOperation} />
				</DropOverActionList>
			);
			default:
				break;
	}

	let content: PreviewMenuContentDefinition;

	switch (view.kind) {
		case "preview-loading":
			content = {
				state: "loading"
			};
			break;
		case "content-too-large":
			content = {
				state: "content-too-large"
			};
			break;
		case "preview":
			content = {
				state: "success",
				onApply: () => applyHandler(view.response),
				onRetry: view.retry,
				previewHtml: view.previewHtml
			};
			break;
		case "preview-error":
			content = {
				state: "error",
				onRetry: view.retry
			};
			break;
		default:
			never(view);
	}

	return (
		<PreviewMenu
			content={content}
			title={generateTitle(view.operation)}
			target={target}
			trackingId={trackingId}
			onClose={onClose}
			targetAlignment={targetAlignment}
			contentAlignment={contentAlignment}
			maxContentLength={maxContentLength}
		/>
	);
};
RewriteToolsMenu.displayName = "RewriteToolsMenu";

type LanguageItemProps = {
	lang: TranslateLangKind;
	onExecute: (operation: RewriteOperation) => void;
};

const TranslateItem = (props: LanguageItemProps): ReactElement => {
	const {
		lang,
		onExecute
	} = props;

	return (
		<ActionListItem
			text={localize(translateLangKindToTitleKeyMap[lang])}
			trackingId={generateTrackingId("translate", `|${lang}`)}
			onClick={() => onExecute({
				kind: "translate",
				lang
			})}
			shouldNotAutoCloseContainingDropOver
		/>
	);
};
TranslateItem.displayName = "TranslateItem";

type ChangeToneItemProps = {
	tone: RewriteToneKind;
	onExecute: (operation: RewriteOperation) => void;
};

const ChangeToneItem = (props: ChangeToneItemProps): ReactElement => {
	const {
		tone,
		onExecute
	} = props;

	return (
		<ActionListItem
			text={localize(rewriteToneKindToTitleKeyMap[tone])}
			trackingId={generateTrackingId("change-tone", `|${tone}`)}
			onClick={
				() => onExecute({
					kind: "change-tone",
					tone
				})
			}
			shouldNotAutoCloseContainingDropOver
		/>
	);
};
ChangeToneItem.displayName = "ChangeToneItem";

type View<TSlice, TContent, TPosition> =
	| "main"
	| "tone"
	| "lang"
	| {
		kind: "preview-loading";
		operation: RewriteOperation;
	}
	| {
		kind: "preview";
		operation: RewriteOperation;
		previewHtml: string;
		response: SuccessfulRewriteToolResponse<TSlice, TContent, TPosition>;
		retry: () => Promise<void>;
	}
	| {
		kind: "preview-error";
		operation: RewriteOperation;
		retry: () => Promise<void>;
	}
	| {
		kind: "content-too-large";
		operation: RewriteOperation;
	};

type RewriteOperation =
	| {
		kind: "make-shorter";
	}
	| {
		kind: "make-longer";
	}
	| {
		kind: "fix-spelling";
	}
	| {
		kind: "change-tone";
		tone: RewriteToneKind;
	}
	| {
		kind: "translate";
		lang: TranslateLangKind;
	};

const rewriteToneKindToTitleKeyMap: { [key in RewriteToneKind]: LocalizationKey; } = {
	[RewriteToneKind.Professional]: "_MoreProfessional",
	[RewriteToneKind.Casual]: "_MoreCasual",
	[RewriteToneKind.Empathic]: "_MoreEmpathic"
};

const translateLangKindToTitleKeyMap: { [key in TranslateLangKind]: LocalizationKey; } = {
	[TranslateLangKind.English]: "_English",
	[TranslateLangKind.Dutch]: "_Dutch",
	[TranslateLangKind.German]: "_German",
	[TranslateLangKind.French]: "_French"
};

function generateTitle(operation: RewriteOperation): string {
	switch (operation.kind) {
		case "make-shorter":
			return localize("_MakeShorter");
		case "make-longer":
			return localize("_MakeLonger");
		case "fix-spelling":
			return localize("_FixSpellingAndGrammar");
		case "change-tone":
			return `${localize("_ChangeTone")} - ${localize(rewriteToneKindToTitleKeyMap[operation.tone])}`;
		case "translate":
			return `${localize("_Translate")} - ${localize(translateLangKindToTitleKeyMap[operation.lang])}`;
		default:
			never(operation);
	}
}
